import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileCategoriesOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Shopping Guide', to: '/shopping-guide' },
    { label: 'Complete Guide', to: '/vigorbuy-spreadsheet-guide' },
  ];

  const isLight = !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="section-container flex items-center justify-between h-16">
        <Link to="/" className="font-heading font-bold text-xl whitespace-nowrap" style={{ color: scrolled ? 'oklch(var(--foreground-950))' : 'oklch(var(--background-50))' }}>
          VigorBuy<span className="font-normal opacity-70">Guide</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                location.pathname === link.to ? 'font-semibold' : ''
              }`}
              style={{
                color: scrolled
                  ? 'oklch(var(--foreground-700))'
                  : 'oklch(var(--background-50) / 0.9)',
              }}
            >
              {link.label}
            </Link>
          ))}
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="btn-primary text-xs px-4 py-2 rounded-md whitespace-nowrap"
          >
            Buy It Now
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i
            className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}
            style={{
              color: isLight && !mobileOpen
                ? 'oklch(var(--background-50))'
                : 'oklch(var(--foreground-950))',
            }}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/60 shadow-sm">
          <div className="section-container py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`text-sm font-medium py-2.5 px-3 rounded-md transition-colors duration-150 ${
                  location.pathname === link.to
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Mobile Categories Accordion */}
            <div className="mt-1">
              <button
                onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
                className="w-full flex items-center justify-between py-2.5 px-3 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-store-2-line"></i>
                  Product Categories
                </span>
                <i className={`${mobileCategoriesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base transition-transform duration-200`}></i>
              </button>

              {mobileCategoriesOpen && (
                <div className="ml-3 mt-1 grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-xs py-2 px-2.5 rounded-md text-foreground-600 hover:bg-accent-50 hover:text-accent-700 transition-colors duration-150 truncate"
                      title={cat.name}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary text-xs w-fit px-5 py-2.5 rounded-md mt-2"
            >
              <i className="ri-shopping-bag-line mr-1.5"></i>
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}